import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"

export const PROCESSING_STATUSES = [
  "mapping",
  "mapping_ready",
  "reconciling",
  "reconcile_ready",
  "answering",
  "review_ready",
  "rendering",
  "answered",
  "sent",
  "failed_mapping",
  "failed_reconcile",
  "failed_answer",
  "failed_render",
] as const

export type ProcessingStatus = null | (typeof PROCESSING_STATUSES)[number]

export type ProcessingStepKey = "mapping" | "reconcile" | "gaps" | "review"

export const PROCESSING_STEP_ORDER: ProcessingStepKey[] = [
  "mapping",
  "reconcile",
  "gaps",
  "review",
]

const IN_FLIGHT_TO_READY: Partial<
  Record<NonNullable<ProcessingStatus>, ProcessingStatus>
> = {
  mapping: "mapping_ready",
  reconciling: "reconcile_ready",
  answering: "review_ready",
  rendering: "answered",
}

/** True for exactly the four statuses an async mock "worker" is running under. */
export function isInFlightStatus(status: ProcessingStatus): boolean {
  return status !== null && status in IN_FLIGHT_TO_READY
}

/** The `_ready` (or terminal) status an in-flight status resolves to, if any. */
export function nextReadyStatus(
  status: ProcessingStatus,
): ProcessingStatus | null {
  if (status === null) return null
  return IN_FLIGHT_TO_READY[status] ?? null
}

/** Which of the 4 UI steps a given processing_status belongs to. */
export function canonicalStepForStatus(
  status: ProcessingStatus,
): ProcessingStepKey {
  switch (status) {
    case null:
    case "mapping":
    case "mapping_ready":
    case "failed_mapping":
      return "mapping"
    case "reconciling":
    case "reconcile_ready":
    case "failed_reconcile":
      return "reconcile"
    case "answering":
    case "review_ready":
    case "failed_answer":
      return "gaps"
    case "rendering":
    case "answered":
    case "sent":
    case "failed_render":
      return "review"
  }
}

/**
 * True if `step` can be viewed given the request's current processing_status —
 * its own canonical step, or any step already passed (read-only revisit).
 *
 * "gaps" and "review" both operate on `review_ready` (Screens 03/04 share a
 * status — advancing between them is a pure client-side nav, no API call) —
 * so "review" must be reachable as soon as "gaps" is, not just once answered.
 */
export function isStepReachable(
  status: ProcessingStatus,
  step: ProcessingStepKey,
): boolean {
  const currentIndex = PROCESSING_STEP_ORDER.indexOf(
    canonicalStepForStatus(status),
  )
  const stepIndex = PROCESSING_STEP_ORDER.indexOf(step)
  if (stepIndex <= currentIndex) return true
  return step === "review" && status === "review_ready"
}

export function isFailedStatus(status: ProcessingStatus): boolean {
  return status !== null && status.startsWith("failed_")
}

// The closed set of reasons the backend records when column mapping fails
// (`mc_answering.types.SKIP_CODES`). Anything else, or no code at all (an older
// backend, a failure the user cannot act on), keeps the generic message.
const FAILED_MESSAGE_KEYS = {
  no_part_column: "MC_QP_FAILED_NO_PART_COLUMN",
  unsupported_file_type: "MC_QP_FAILED_UNSUPPORTED_FILE_TYPE",
  no_table: "MC_QP_FAILED_NO_TABLE",
} as const satisfies Record<string, TranslationKeys>

export function failedMessageKey(
  failureCode: string | null | undefined,
): TranslationKeys {
  // Own keys only: "constructor" or "__proto__" would otherwise return what
  // every object inherits, which is not a message key.
  return failureCode && Object.hasOwn(FAILED_MESSAGE_KEYS, failureCode)
    ? FAILED_MESSAGE_KEYS[failureCode as keyof typeof FAILED_MESSAGE_KEYS]
    : "MC_QP_FAILED_MESSAGE"
}
